package com.arenaproplay.admin.domain.admin

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class BehaviorKpis(
    @SerialName("activeUsers") val activeUsers: KpiDelta,
    @SerialName("sessions") val sessions: KpiDelta,
    @SerialName("avgSessionMinutes") val avgSessionMinutes: KpiDelta,
    @SerialName("totalEvents") val totalEvents: KpiDelta,
)

@Serializable
data class TopScreenRow(
    @SerialName("screen") val screen: String,
    @SerialName("views") val views: Long,
    @SerialName("uniqueUsers") val uniqueUsers: Long,
)

@Serializable
data class TopActionRow(
    @SerialName("action") val action: String,
    @SerialName("count") val count: Long,
    @SerialName("uniqueUsers") val uniqueUsers: Long,
)

@Serializable
data class CountryUsageRow(
    @SerialName("country") val country: String,
    @SerialName("users") val users: Long,
    @SerialName("events") val events: Long,
)

@Serializable
data class DeviceShareRow(
    @SerialName("platform") val platform: String,
    @SerialName("deviceType") val deviceType: String,
    @SerialName("users") val users: Long,
)

@Serializable
data class JourneyPathRow(
    @SerialName("path") val path: String,
    @SerialName("count") val count: Long,
)

@Serializable
data class NewUserFunnelStep(
    @SerialName("key") val key: String,
    @SerialName("devices") val devices: Long,
    @SerialName("firstSessionDevices") val firstSessionDevices: Long,
)

@Serializable
data class NewUserFunnel(
    @SerialName("installs") val installs: Long,
    @SerialName("steps") val steps: List<NewUserFunnelStep>,
    @SerialName("signupsByProvider") val signupsByProvider: Map<String, Long> = emptyMap(),
)

@Serializable
data class TimelineEvent(
    @SerialName("eventType") val eventType: String,
    @SerialName("screen") val screen: String? = null,
    @SerialName("platform") val platform: String,
    @SerialName("sessionId") val sessionId: String? = null,
    @SerialName("metadata") val metadata: String? = null,
    @SerialName("occurredAt") val occurredAt: Long,
    @SerialName("country") val country: String? = null,
)

@Serializable
data class OnlineNow(
    @SerialName("online") val online: Int,
    @SerialName("activeLast5m") val activeLast5m: Long,
    @SerialName("activeByPlatform") val activeByPlatform: Map<String, Long> = emptyMap(),
)

/** One step in a session path — type and human-readable label. */
@Serializable
data class SessionStep(
    @SerialName("type") val type: String,
    @SerialName("label") val label: String,
)

/**
 * One row in the admin sessions list — mirrors backend [SessionRowDto] verbatim.
 *
 * Field names are the source of truth negotiated with the backend; do not rename
 * without a coordinated backend change (feature/session-list).
 */
@Serializable
data class SessionRow(
    @SerialName("sessionId") val sessionId: String,
    @SerialName("userId") val userId: String? = null,
    @SerialName("userName") val userName: String? = null,
    @SerialName("deviceId") val deviceId: String? = null,
    @SerialName("country") val country: String? = null,
    @SerialName("platform") val platform: String,
    /** Epoch millis of the first event in the session. */
    @SerialName("startedAt") val startedAt: Long,
    @SerialName("durationSeconds") val durationSeconds: Long,
    @SerialName("eventCount") val eventCount: Long,
    @SerialName("screensCount") val screensCount: Long,
    @SerialName("city") val city: String? = null,
    @SerialName("source") val source: String? = null,
    @SerialName("device") val device: String? = null,
    @SerialName("browser") val browser: String? = null,
    @SerialName("isNew") val isNew: Boolean = false,
    @SerialName("isOnline") val isOnline: Boolean = false,
    @SerialName("steps") val steps: List<SessionStep> = emptyList(),
)

/** Paginated list of sessions — mirrors backend [SessionListDto]. */
@Serializable
data class SessionList(
    @SerialName("items") val items: List<SessionRow>,
    /** Total sessions in the period window (independent of limit/offset). */
    @SerialName("total") val total: Long,
)

// ── Гео-диагностика (admin/behavior/debug/geo) ────────────────────────────────

@Serializable
data class GeoProviderResult(
    @SerialName("provider") val provider: String,
    @SerialName("httpStatus") val httpStatus: String,
    @SerialName("city") val city: String? = null,
    @SerialName("countryCode") val countryCode: String? = null,
    @SerialName("error") val error: String? = null,
)

@Serializable
data class GeoLastAttempt(
    @SerialName("ip") val ip: String,
    @SerialName("providerResults") val providerResults: List<GeoProviderResult> = emptyList(),
    @SerialName("timestamp") val timestamp: String,
)

@Serializable
data class GeoSnapshot(
    @SerialName("totalAttempts") val totalAttempts: Long,
    @SerialName("totalSuccesses") val totalSuccesses: Long,
    @SerialName("lastAttempt") val lastAttempt: GeoLastAttempt? = null,
)

@Serializable
data class GeoDebug(
    @SerialName("callerIp") val callerIp: String? = null,
    @SerialName("xffHeader") val xffHeader: String? = null,
    @SerialName("remoteAddress") val remoteAddress: String,
    @SerialName("lookup") val lookup: List<GeoProviderResult> = emptyList(),
    @SerialName("snapshot") val snapshot: GeoSnapshot,
)
